import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide Hook for Journalist My Learning Page
 *
 * Four steps, one per tab: Enrolled, Completed, Watch Later, Certificates.
 */
export const useJournalistMyLearningTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnMyLearning = pathname === ROUTE_NAMES.JOURNALIST_LEARNING;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "journalistMyLearning",
      enabled: isOnMyLearning,
      steps: [
        {
          id: "journalist-my-learning-enrolled",
          targetSelector: '[data-tour-item="journalist-my-learning-enrolled"]',
          title: t("tourGuide.journalistMyLearning.step1.title"),
          description: t("tourGuide.journalistMyLearning.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-my-learning-completed",
          targetSelector: '[data-tour-item="journalist-my-learning-completed"]',
          title: t("tourGuide.journalistMyLearning.step2.title"),
          description: t("tourGuide.journalistMyLearning.step2.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-my-learning-watch-later",
          targetSelector:
            '[data-tour-item="journalist-my-learning-watch-later"]',
          title: t("tourGuide.journalistMyLearning.step3.title"),
          description: t("tourGuide.journalistMyLearning.step3.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "journalist-my-learning-certificates",
          targetSelector:
            '[data-tour-item="journalist-my-learning-certificates"]',
          title: t("tourGuide.journalistMyLearning.step4.title"),
          description: t("tourGuide.journalistMyLearning.step4.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
      ],
    }),
    [isOnMyLearning, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.JournalistMyLearning, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const journalistMyLearningTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.JournalistMyLearning,
    isOnTourPath: isOnMyLearning && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (journalistMyLearningTourGuide.isActive) {
      setActiveTourModule("journalistMyLearning");
      setCurrentTourStep(journalistMyLearningTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    journalistMyLearningTourGuide.isActive,
    journalistMyLearningTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    journalistMyLearningTourGuide,
    isMobile,
  };
};
